import { FC, ReactNode } from 'react';
import { differenceInDays, differenceInHours, isBefore, parseISO } from 'date-fns';
import { Box, Text } from 'roo-ui/components';
import { CancellationWindow } from 'lib/graphql/generated';
import { getPendingScheduledPayment } from 'lib/bookingInfo';
import { DetailsBooking } from 'types';
import { FormattedDate } from 'components/Formatters';
import Timeline from 'components/Timeline';
import { getRefundableText } from './helpers';

// TODO: Cleanup these file and simplify logic
const createSchedulePaymentItem = (booking: DetailsBooking) => {
  if (!booking.flags.hasPendingScheduledPayment) {
    return null;
  }

  const scheduledPayment = getPendingScheduledPayment(booking)!;
  const daysBeforeCheckIn = differenceInDays(parseISO(booking.reservation.checkInDateTime), parseISO(scheduledPayment.deferredTo));
  const timeToCheckInDescription = `${daysBeforeCheckIn} day${daysBeforeCheckIn === 1 ? '' : 's'} before check-in`;
  const additionalInfo = `Final scheduled payment will be automatically charged to ${scheduledPayment.creditCardPaymentAllocation.paymentMethod.cardType} credit card ending ${scheduledPayment.creditCardPaymentAllocation.paymentMethod.lastFourDigits}.`;

  return (
    <Timeline.Item
      data-testid="schedule-payment-timeline-item"
      key="schedule-payment-item"
      iconColor="green"
      state="completed"
      title={<FormattedDate date={scheduledPayment.deferredTo} />}
      description={
        <>
          <Text as="div">{timeToCheckInDescription}</Text>
          <Text as="div">{additionalInfo}</Text>
          <Text as="div">Full refund</Text>
        </>
      }
    />
  );
};

const getRefundDescription = (
  cancellationWindow: CancellationWindow,
  checkInDateTime: string,
  showDaysCount: boolean,
  additionalInfo: string | ReactNode | undefined,
) => {
  const daysBeforeCheckIn = differenceInDays(parseISO(checkInDateTime), parseISO(cancellationWindow.startTime));
  const hoursBeforeCheckIn = differenceInHours(parseISO(checkInDateTime), parseISO(cancellationWindow.startTime));
  const timeToCheckInDescription =
    daysBeforeCheckIn > 0
      ? `${daysBeforeCheckIn} day${daysBeforeCheckIn === 1 ? '' : 's'} before check-in`
      : `${hoursBeforeCheckIn} hour${hoursBeforeCheckIn === 1 ? '' : 's'} before check-in`;
  return (
    <>
      {showDaysCount && <Text as="div">{timeToCheckInDescription}</Text>}
      {additionalInfo && <Text as="div">{additionalInfo}</Text>}
      <Text as="div">{getRefundableText(cancellationWindow.cancellationType)}</Text>
    </>
  );
};

const createRefundTimelineItems = (booking: DetailsBooking) => {
  const scheduledPayment = getPendingScheduledPayment(booking);
  let shouldIncludedSchedulePayment = !!scheduledPayment;

  return (
    <>
      {booking.reservation.cancellationPolicy!.cancellationWindows.reduce<ReactNode[]>((items, cancellationWindow, index, windows) => {
        const shouldIncludeCancellationWindowItem = isBefore(
          parseISO(cancellationWindow.startTime),
          parseISO(booking.reservation.checkInDateTime),
        );
        const isLastWindow = index === windows.length - 1;
        const additionalInfo = index === 0 ? <FormattedDate date={booking.createdAt} /> : undefined;
        const showDaysCount = index !== 0;
        const timelineItem = shouldIncludeCancellationWindowItem ? (
          <Timeline.Item
            key={index}
            iconName={index === 0 ? 'confirmation' : undefined}
            iconColor={index === 0 ? 'green' : undefined}
            state={cancellationWindow.isFullyRefundable ? 'completed' : 'pending'}
            title={index === 0 ? 'Reservation confirmed' : <FormattedDate date={cancellationWindow.startTime} />}
            description={getRefundDescription(cancellationWindow, booking.reservation.checkInDateTime, showDaysCount, additionalInfo)}
          />
        ) : null;

        switch (true) {
          case shouldIncludedSchedulePayment && isBefore(parseISO(scheduledPayment!.deferredTo), parseISO(cancellationWindow.startTime)):
            shouldIncludedSchedulePayment = false;
            return [...items, createSchedulePaymentItem(booking), timelineItem];
          case shouldIncludedSchedulePayment && isLastWindow:
            shouldIncludedSchedulePayment = false;
            return [...items, timelineItem, createSchedulePaymentItem(booking)];
          default:
            return [...items, timelineItem];
        }
      }, [])}
    </>
  );
};

const PolicyTimeline: FC<{ booking: DetailsBooking }> = ({ booking }) =>
  booking.reservation.cancellationPolicy?.cancellationWindows[0].cancellationType === 'NON_REFUNDABLE' ? null : (
    <Box data-testid="cancellation-policy-timeline" py={8} px={5}>
      <Timeline>
        {createRefundTimelineItems(booking)}
        <Timeline.Item
          iconName="home"
          iconProps={{
            color: 'white',
            bg: 'puertoRico',
            size: 20,
            p: '2px',
            ml: '-11px',
          }}
          state="pending"
          title={<FormattedDate date={booking.reservation.checkInDateTime} />}
          description={`Check-in to ${booking.reservation.property.name}`}
        />
      </Timeline>
    </Box>
  );

export default PolicyTimeline;
